import { useMemo, useState, useEffect } from 'react';
import { Box, Paper, Stack, Checkbox, Typography, Chip, Pagination } from '@mui/material';
import { CheckCircle, ErrorOutline } from '@mui/icons-material';
import { votoColor, votoLabel, distanceM, formatDist, IGNOTA } from '../lib/parse';
import { NearMe } from '@mui/icons-material';

const PER_PAGE = 25;

function StatoBadge({ stato }) {
  const rev = stato === 'revisionato';
  return (
    <Chip size="small" icon={rev ? <CheckCircle /> : <ErrorOutline />} label={rev ? 'Revisionato' : 'Da rivedere'}
          sx={{ bgcolor: rev ? '#ecfdf5' : '#fff7ed', color: rev ? '#15803d' : '#c2410c',
                '& .MuiChip-icon': { color: 'inherit' }, fontWeight: 700 }} />
  );
}

export default function ListPanel({ items, selected, setSelected, onOpen, userPos, vedeVoto = true, mostraConc = vedeVoto }) {
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [items]);

  const pages = Math.max(1, Math.ceil(items.length / PER_PAGE));
  const slice = useMemo(() => items.slice((page - 1) * PER_PAGE, page * PER_PAGE), [items, page]);

  const toggleSel = (id, on) => setSelected(prev => {
    const next = new Set(prev);
    on ? next.add(id) : next.delete(id);
    return next;
  });

  return (
    <Stack sx={{ height: '100%', minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.5, py: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{selected.size} selezionati</Typography>
        <Typography variant="body2" color="text.secondary">{items.length} risultati</Typography>
      </Stack>

      <Stack spacing={1} sx={{ px: 1.5, overflowY: 'auto', flex: 1, pb: 1 }}>
        {slice.map(({ raw, meta }) => {
          const vc = votoColor(meta.voto);
          const bordo = !vedeVoto ? '#e2e8f0' : meta.stato === 'revisionato' ? '#bbe7c9' : '#fed7aa';
          const dist = userPos ? distanceM(userPos.lat, userPos.lon, raw.latitude, raw.longitude) : null;
          return (
            <Paper key={raw.id} onClick={() => onOpen(raw.id)}
                   sx={{ p: 1.2, border: `1.5px solid ${bordo}`, cursor: 'pointer',
                         '&:hover': { boxShadow: '0 3px 10px rgba(15,23,42,.12)' } }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Checkbox size="small" sx={{ p: 0.3, mt: 0.2 }} checked={selected.has(raw.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => toggleSel(raw.id, e.target.checked)} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={0.7} alignItems="center" flexWrap="wrap" useFlexGap>
                    {mostraConc && meta.concessionaria && (
                      <Chip size="small" variant="outlined" label={meta.concessionaria}
                            sx={{ height: 20, fontSize: 11, ...(meta.concessionaria === IGNOTA
                              ? { color: '#64748b', borderColor: '#cbd5e1' } : { color: '#1d4ed8', borderColor: '#bfdbfe' }) }} />
                    )}
                    {vedeVoto && (
                      <Chip size="small" label={votoLabel(meta.voto)}
                            sx={{ height: 20, fontSize: 11, fontWeight: 800, bgcolor: vc.bg, color: vc.fg }} />
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {raw.title || `id ${raw.id}`}
                    </Typography>
                  </Stack>
                  {raw.nomination && (
                    <Typography variant="caption" color="text.secondary" noWrap display="block">{raw.nomination}</Typography>
                  )}
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                    {meta.tags.map(t => (
                      <Chip key={t} size="small" variant="outlined" label={t}
                            sx={{ height: 18, fontSize: 10, color: '#64748b' }} />
                    ))}
                  </Stack>
                </Box>
                <Stack alignItems="flex-end" spacing={0.5}>
                  {vedeVoto && <StatoBadge stato={meta.stato} />}
                  {dist != null && isFinite(dist) && (
                    <Chip size="small" icon={<NearMe />} label={formatDist(dist)}
                          sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: '#eff6ff', color: '#1d4ed8',
                                '& .MuiChip-icon': { color: 'inherit', fontSize: 13 } }} />
                  )}
                </Stack>
              </Stack>
            </Paper>
          );
        })}
        {!slice.length && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            Nessun cartellone corrisponde a ricerca/filtri.
          </Typography>
        )}
      </Stack>

      {pages > 1 && (
        <Stack alignItems="center" sx={{ py: 1, borderTop: '1px solid #e2e8f0' }}>
          <Pagination size="small" count={pages} page={page} onChange={(_, p) => setPage(p)} />
        </Stack>
      )}
    </Stack>
  );
}
